import html2canvas from 'html2canvas';
import i18n from 'i18next';
import { latexToText } from 'src/app/utils/latexToText';

export type EquationPopup = {
  isVisible: boolean;
  position: { x: number; y: number };
  htmlContent: string;
  targetElement: Element | null;
  isDisplayMode: boolean;
  isLongEquation: boolean;
};

/**
 * Check if an equation is long and causes horizontal overflow
 * Uses a more reliable method by checking actual overflow
 */
export const isEquationLong = (element: Element, container: HTMLElement): boolean => {
  try {
    // Get the container's available width
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;

    // Get the element's bounding rect
    const elementRect = element.getBoundingClientRect();

    // Method 1: Check if element extends beyond container bounds
    const extendsContainer = elementRect.right > containerRect.right;

    // Method 2: Check if element causes container to have horizontal scroll
    const originalOverflow = container.style.overflow;
    const hasHorizontalScroll = container.scrollWidth > container.clientWidth;

    // Method 3: Check element's height - if it spans multiple lines (for inline equations)
    const katexElement = element.classList.contains('katex') ? element : element.closest('.katex');
    let isMultiLine = false;

    if (katexElement && !katexElement.classList.contains('katex-display')) {
      // For inline equations, check if height is unusually large (indicating line breaks)
      const computedStyle = window.getComputedStyle(katexElement);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
      const elementHeight = elementRect.height;
      isMultiLine = elementHeight > lineHeight * 1.5; // More than 1.5 line heights
    }

    // Method 4: Check if element width is significantly larger than container
    const significantlyWider = elementRect.width > containerWidth * 0.8;

    return extendsContainer || hasHorizontalScroll || isMultiLine || significantlyWider;
  } catch (error) {
    console.warn('Error checking equation length:', error);
    return false;
  }
};

/**
 * Calculate optimal popup position within viewport bounds
 */
export const calculatePopupPosition = (
  rect: DOMRect,
  popupWidth: number = 200,
  popupHeight: number = 100
): { x: number; y: number } => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const offsetX = 10;
  const offsetY = 10;

  let x = rect.left + offsetX;
  let y = rect.bottom + offsetY;

  // Ensure popup stays within viewport bounds
  if (x + popupWidth > viewportWidth) {
    x = Math.max(0, rect.right - popupWidth - offsetX);
  }

  if (y + popupHeight > viewportHeight) {
    y = Math.max(0, rect.top - popupHeight - offsetY);
  }

  // Final bounds check
  x = Math.max(0, Math.min(x, viewportWidth - popupWidth - 5));
  y = Math.max(0, Math.min(y, viewportHeight - popupHeight - 5));

  return { x, y };
};

/**
 * Extract equation content and metadata from element
 */
export const extractEquationData = (element: Element) => {
  let htmlContent = '';
  let isDisplayMode = false;

  if (element.classList.contains('katex') || element.closest('.katex')) {
    const katexElement = element.classList.contains('katex') ? element : element.closest('.katex');
    if (katexElement) {
      isDisplayMode = katexElement.classList.contains('katex-display');
      htmlContent = katexElement.outerHTML;
    } else {
      htmlContent = element.outerHTML;
    }
  } else {
    htmlContent = element.outerHTML;
  }

  return { htmlContent, isDisplayMode };
};

/**
 * Toggle display mode of a KaTeX element
 */
export const toggleEquationDisplayMode = (element: Element): boolean => {
  const katexElement = element.classList.contains('katex') ? element : element.closest('.katex');

  if (!katexElement) return false;

  const isCurrentlyDisplay = katexElement.classList.contains('katex-display');

  if (isCurrentlyDisplay) {
    katexElement.classList.remove('katex-display');
  } else {
    katexElement.classList.add('katex-display');
  }

  return !isCurrentlyDisplay; // Return new display mode state
};

/**
 * Show a temporary message to user
 */
export const showMessage = (text: string, className: string) => {
  const message = document.createElement('div');
  message.className = className;
  message.textContent = text;
  document.body.appendChild(message);

  setTimeout(() => {
    if (document.body.contains(message)) {
      document.body.removeChild(message);
    }
  }, 2000);
};

/**
 * Create and display equation as image in modal
 */
export const openEquationAsImage = async (
  htmlContent: string,
  modalClassName: string,
  onError: (error: string) => void
): Promise<void> => {
  try {
    if (!htmlContent) {
      onError('No equation content available');
      return;
    }

    // Remove existing modal if any
    const existingModal = document.getElementById('equation-modal');
    if (existingModal) {
      document.body.removeChild(existingModal);
      // Restore aria-hidden on background content
      const hiddenElements = document.querySelectorAll('[data-aria-hidden-backup]');
      hiddenElements.forEach((el) => {
        const originalValue = el.getAttribute('data-aria-hidden-backup');
        if (originalValue === null) {
          el.removeAttribute('aria-hidden');
        } else {
          el.setAttribute('aria-hidden', originalValue);
        }
        el.removeAttribute('data-aria-hidden-backup');
      });
    }

    // Store the element that had focus before modal opened
    const previouslyFocusedElement = document.activeElement as HTMLElement;

    // Hide all background content from screen readers
    const allChildren = Array.from(document.body.children);
    allChildren.forEach((child) => {
      if (child.id !== 'equation-modal' && child.id !== 'hidden-equation') {
        const element = child as HTMLElement;
        // Store original aria-hidden value
        const originalAriaHidden = element.getAttribute('aria-hidden');
        element.setAttribute('data-aria-hidden-backup', originalAriaHidden || '');
        element.setAttribute('aria-hidden', 'true');
      }
    });

    // Create modal backdrop
    const modal = document.createElement('div');
    modal.id = 'equation-modal';
    modal.className = modalClassName;

    // Add ARIA attributes for screen readers
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'equation-modal-title');
    modal.setAttribute('aria-describedby', 'equation-modal-description');
    modal.setAttribute('tabindex', '-1'); // Make focusable for programmatic focus

    // Lock body scroll when modal opens
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    document.body.appendChild(modal);

    // Create or get hidden element for rendering
    let hiddenEquation = document.getElementById('hidden-equation') as HTMLElement;
    if (!hiddenEquation) {
      hiddenEquation = document.createElement('div');
      hiddenEquation.id = 'hidden-equation';
      hiddenEquation.setAttribute('aria-hidden', 'true');
      // Use opacity instead of position absolute
      hiddenEquation.style.cssText = `
        opacity: 0;
        pointer-events: none;
        position: fixed;
        top: 0;
        left: 0;
        z-index: -1000;
        padding: 20px;
        background: white;
        font-size: 1rem;
        width: auto;
        height: auto;
        max-width: none;
        white-space: nowrap;
        overflow: visible;
      `;
      document.body.appendChild(hiddenEquation);
    }

    // Clear and set content
    hiddenEquation.innerHTML = htmlContent;

    // Temporarily make visible for measurement and rendering
    hiddenEquation.style.opacity = '1';
    hiddenEquation.style.zIndex = '1000';

    // Handle display mode and displaystyle
    const katexElement = hiddenEquation.querySelector('.katex');
    if (katexElement) {
      // Always force display mode for image generation
      katexElement.classList.add('katex-display');

      // Handle \displaystyle by ensuring proper styling
      const mathElements = katexElement.querySelectorAll('.mstyle, .base');
      mathElements.forEach((el) => {
        (el as HTMLElement).style.fontSize = '1.2em';
        (el as HTMLElement).style.display = 'inline-block';
      });

      // Set container styles for better rendering
      katexElement.setAttribute(
        'style',
        `
        font-size: 1.2em !important;
        display: block !important;
        text-align: center !important;
        margin: 0 !important;
        padding: 10px !important;
      `
      );
    }

    // Calculate dimensions dynamically
    const rect = hiddenEquation.getBoundingClientRect();
    const width = Math.max(hiddenEquation.scrollWidth, rect.width) + 40;
    const height = Math.max(hiddenEquation.scrollHeight, rect.height) + 40;

    // Generate image with calculated dimensions
    const canvas = await html2canvas(hiddenEquation, {
      scale: 3,
      useCORS: true,
      backgroundColor: 'white',
      width: width,
      height: height,
      scrollX: 0,
      scrollY: 0,
      windowWidth: width,
      windowHeight: height,
      logging: false,
      allowTaint: true,
      foreignObjectRendering: true,
    });

    // Hide the element again
    hiddenEquation.style.opacity = '0';
    hiddenEquation.style.zIndex = '-1000';

    const imageUrl = canvas.toDataURL('image/png');

    // Create modal title (visually hidden but accessible to screen readers)
    const modalTitle = document.createElement('h2');
    modalTitle.id = 'equation-modal-title';
    modalTitle.textContent = i18n.t('EQUATION_IMAGE_MODAL_TITLE') || 'Equation Image Viewer';
    modalTitle.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    `;
    modal.appendChild(modalTitle);

    // Create instruction text with proper semantic structure
    const instructionText = document.createElement('div');
    instructionText.id = 'equation-modal-description';
    instructionText.setAttribute('role', 'status');
    instructionText.setAttribute('aria-live', 'polite');
    instructionText.innerHTML = `
      <div style='color: white; margin-bottom: 20px; font-size: 1rem; text-align: center; max-width: 90%; word-wrap: break-word;'>
        <p style='margin: 0 0 8px 0;'>
          ${i18n.t('RIGHT_CLICK_IMAGE')}
        </p>
        <p style='margin: 0;'>
          ${i18n.t('CLICK_OUTSIDE')}
        </p>
      </div>
    `;
    modal.appendChild(instructionText);

    // Create image container
    const imgContainer = document.createElement('div');
    imgContainer.style.cssText = `
      background-color: white;
      padding: 20px;
      border-radius: 8px;
      max-width: 90%;
      max-height: 80%;
      display: flex;
      justify-content: center;
      align-items: center;
    `;

    const img = document.createElement('img');
    img.id = 'equation-image';
    img.src = imageUrl;
    img.alt = i18n.t('EQUATION_IMAGE_ALT') || 'Mathematical equation image';
    img.setAttribute('role', 'img');
    img.setAttribute('tabindex', '0'); // Make image focusable
    img.style.cssText = `
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    `;

    imgContainer.appendChild(img);
    modal.appendChild(imgContainer);

    // Get all focusable elements within the modal
    const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ];

      const elements: HTMLElement[] = [];
      focusableSelectors.forEach((selector) => {
        const found = Array.from(container.querySelectorAll(selector)) as HTMLElement[];
        elements.push(...found);
      });

      // Filter out elements that are not visible
      return elements.filter((el) => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
      });
    };

    // Focus trap implementation
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements(modal);

      if (focusableElements.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement || document.activeElement === modal) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    // Close modal function
    const closeModal = () => {
      // Restore body scroll
      document.body.style.overflow = originalOverflow || '';

      // Remove event listeners
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', trapFocus);

      // Restore aria-hidden on background content
      const hiddenElements = document.querySelectorAll('[data-aria-hidden-backup]');
      hiddenElements.forEach((el) => {
        const originalValue = el.getAttribute('data-aria-hidden-backup');
        if (originalValue === null || originalValue === '') {
          el.removeAttribute('aria-hidden');
        } else {
          el.setAttribute('aria-hidden', originalValue);
        }
        el.removeAttribute('data-aria-hidden-backup');
      });

      // Remove modal
      if (document.body.contains(modal)) {
        document.body.removeChild(modal);
      }

      // Return focus to previously focused element
      if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
        try {
          previouslyFocusedElement.focus();
        } catch (e) {
          // If focus fails, focus body as fallback
          document.body.focus();
        }
      }
    };

    // Keyboard event handler for Escape key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeModal();
      }
    };

    // Add keyboard event listeners
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', trapFocus);

    // Focus management - focus the modal when it opens
    // Use setTimeout to ensure modal is fully rendered
    setTimeout(() => {
      modal.focus();
    }, 0);

    // Event handlers
    modal.addEventListener('click', (event) => {
      if (
        event.target === modal ||
        event.target === instructionText ||
        (event.target as HTMLElement).closest('#equation-modal-description')
      ) {
        closeModal();
      }
    });

    imgContainer.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    // Prevent clicks on the image from closing the modal
    img.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    // Handle right-click context menu on image
    img.addEventListener('contextmenu', (event) => {
      event.stopPropagation();
      // Allow default context menu for right-click to copy/save
    });
  } catch (error) {
    console.error('Error generating equation image:', error);
    onError('Error generating image');

    // Restore body scroll on error
    document.body.style.overflow = '';

    // Restore aria-hidden on background content
    const hiddenElements = document.querySelectorAll('[data-aria-hidden-backup]');
    hiddenElements.forEach((el) => {
      const originalValue = el.getAttribute('data-aria-hidden-backup');
      if (originalValue === null || originalValue === '') {
        el.removeAttribute('aria-hidden');
      } else {
        el.setAttribute('aria-hidden', originalValue);
      }
      el.removeAttribute('data-aria-hidden-backup');
    });

    // Clean up modal if it was created
    const modal = document.getElementById('equation-modal');
    if (modal && document.body.contains(modal)) {
      document.body.removeChild(modal);
    }

    // Clean up hidden element
    const hiddenEquation = document.getElementById('hidden-equation');
    if (hiddenEquation) {
      hiddenEquation.style.opacity = '0';
      hiddenEquation.style.zIndex = '-1000';
    }
  }
};

/**
 * Clean up equation-related DOM elements
 */
export const cleanupEquationElements = () => {
  const hiddenEquation = document.getElementById('hidden-equation');
  if (hiddenEquation && hiddenEquation.parentNode) {
    hiddenEquation.parentNode.removeChild(hiddenEquation);
  }

  const modal = document.getElementById('equation-modal');
  if (modal && modal.parentNode) {
    modal.parentNode.removeChild(modal);
  }
};
